import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from 'react-redux';
import octopusInstance from "../../services/octopus/octopusInstance";
import { getFormattedPosition } from "../../utils/portfolio";
import { getPositionsValuesFormatted } from "../../utils/portfolio/portfolioKeyValueFormatter";
import { getExchangeCode } from "../../services/exchanges";

function usePositionUpdates() {
  const dispatch = useDispatch();
  const currentPositions = useSelector(state => state.portfolio.arrPositionsData);
  const positionType = useSelector(state => state.portfolio.positionType);
  const fundsViewV2 = useSelector(state => state.funds.fundsViewV2);
  const positionsRef = useRef(null);
  const positionTypeRef = useRef('historical');
  const positionHandlerRef = useRef(null);
  const marketDataHandlersRef = useRef([]);
  const setupAttempts = useRef(0);
  // Signature of the currently-subscribed positions; only resubscribe when
  // the set of (exchange, instrument_token) actually changes. Otherwise
  // every Redux dispatch (new array ref) would tear down + recreate every
  // subscription causing continuous compact_marketdata flapping.
  const lastSubscriptionSigRef = useRef('');
  const auth = useSelector((state) => state.auth);
  
  // PnL calculation refs
  const prevTotalPnLRef = useRef(0);
  const prevRealizedPnlRef = useRef(0);
  const prevUnrealizedPnlRef = useRef(0);
  const prevPositionsLengthRef = useRef(0);

  // Keep refs updated with latest values
  useEffect(() => {
    // Check if new positions were added
    const hasNewPositions = currentPositions.length > prevPositionsLengthRef.current;
    positionsRef.current = currentPositions;
    positionTypeRef.current = positionType;
    prevPositionsLengthRef.current = currentPositions.length;

    // If new positions were added, recalculate P&L immediately
    if (hasNewPositions) {
      calculateAndUpdatePnL();
    }

    // Resubscribe only when the actual instrument set changes — not on
    // every Redux dispatch that produces a new array ref with same contents.
    const sig = (currentPositions || [])
      .map((p) => `${p?.exchange || ''}:${p?.instrument_token || ''}:${p?.instrument_identifier || ''}`)
      .sort()
      .join(',');
    if (sig !== lastSubscriptionSigRef.current) {
      lastSubscriptionSigRef.current = sig;
      setupMarketDataSubscriptions();
    }
  }, [currentPositions, positionType]);

  // Function to calculate P&L similar to the one in usePnLCalculator.js
  const calculateAndUpdatePnL = () => {
    console.log('Calculating P&L for', positionsRef.current?.length || 0, 'positions');
    
    if (!positionsRef.current || !positionsRef.current.length) {
      // When no positions, set all P&L to zero
      const realizedPnl = 0;
      const unrealizedPnl = 0;
      const totalPnL = 0;
      
      console.log('No positions found, setting P&L to zero');
      
      if (prevTotalPnLRef.current !== totalPnL || 
          prevRealizedPnlRef.current !== realizedPnl || 
          prevUnrealizedPnlRef.current !== unrealizedPnl) {
        
        console.log('P&L Update - Zero positions:', { totalPnL, realizedPnl, unrealizedPnl });
        
        dispatch({
          type: "SET_PNL_UPDATE",
          payload: {
            totalPnl: totalPnL,
            realizedPnl: realizedPnl,
            unrealizedPnl: unrealizedPnl
          }
        });
        prevTotalPnLRef.current = totalPnL;
        prevRealizedPnlRef.current = realizedPnl;
        prevUnrealizedPnlRef.current = unrealizedPnl;
      }
      return;
    }

    // Calculate PnL components individually without creating state
    let realizedPnl = 0; // Always start with zero
    let unrealizedPnl = 0;
    
    for (let i = 0; i < positionsRef.current.length; i++) {
      const position = positionsRef.current[i];
      
      // Ensure we have a valid LTP value
      const ltp = (position.ltp !== undefined && position.ltp !== null && position.ltp > 0) 
        ? position.ltp 
        : (position.previous_close || 0);
      
      // Set realized PnL to zero in the position data before formatting
      const positionWithZeroRealizedPnL = {
        ...position,
        realizedPnL: 0,
        realised_mtm: 0,
        realized_mtm: 0
      };
      
      // Use getFormattedPosition with the correct LTP parameter
      const currentPosition = getFormattedPosition(
        positionWithZeroRealizedPnL,
        ltp,  // Pass LTP as the second parameter
        positionTypeRef.current
      );
      
      // Add to component totals - only use unrealized PnL, keep realized at zero
      unrealizedPnl += Number(currentPosition.unrealizedPnL || currentPosition.unrealised_mtm || 0);
      
      // Update the position in the array with zeroed realized PnL
      positionsRef.current[i] = {
        ...currentPosition,
        realizedPnL: 0,
        realised_mtm: 0,
        realized_mtm: 0
      };
    }
    
    // Ensure we're working with exact numbers
    realizedPnl = 0; // Always zero
    unrealizedPnl = parseFloat(unrealizedPnl.toFixed(2));
    
    // Calculate total PnL explicitly as the sum of the components
    const totalPnL = unrealizedPnl; // Since realizedPnl is always 0

    // Only dispatch if values actually changed
    if (Math.abs(totalPnL - prevTotalPnLRef.current) > 0.0001 || 
        Math.abs(unrealizedPnl - prevUnrealizedPnlRef.current) > 0.0001) {
      
      console.log('P&L Update - Active positions:', { 
        positionCount: positionsRef.current.length,
        totalPnL, 
        realizedPnl: 0, 
        unrealizedPnl,
        prevTotal: prevTotalPnLRef.current,
        change: totalPnL - prevTotalPnLRef.current
      });
      
      dispatch({
        type: "SET_PNL_UPDATE",
        payload: {
          totalPnl: totalPnL,
          realizedPnl: 0, // Always zero
          unrealizedPnl: unrealizedPnl
        }
      });
      
      prevTotalPnLRef.current = totalPnL;
      prevRealizedPnlRef.current = 0; // Always zero
      prevUnrealizedPnlRef.current = unrealizedPnl;
    }
  };

  // Setup market data subscriptions for live LTP updates
  const setupMarketDataSubscriptions = () => {
    // Clear old handlers
    if (marketDataHandlersRef.current.length) {
      console.log('Clearing previous market data subscriptions:', marketDataHandlersRef.current.length);
      marketDataHandlersRef.current.forEach(handler => {
        if (handler && typeof handler.unsubscribe === 'function') {
          handler.unsubscribe();
        }
      });
      marketDataHandlersRef.current = [];
    }

    if (!positionsRef.current || !positionsRef.current.length) {
      console.log('No positions available, skipping market data subscriptions');
      return;
    }

    console.log('Setting up market data subscriptions for', positionsRef.current.length, 'positions');

    // Create a positions map to ensure updates go to the correct position
    const positionsMap = {};
    positionsRef.current.forEach((position, index) => {
      if (position.token && position.exchange && position.product) {
        const key = `${position.exchange}-${position.token}-${position.product}`;
        positionsMap[key] = index;
      }
    });

    // Create handlers for each position
    marketDataHandlersRef.current = positionsRef.current.map((position) => {
      // Skip if position doesn't have required fields
      if (!position.token || !position.exchange || !position.instrument_token) {
        return null;
      }

      // Use a stable subscription location that includes identifying information
      const subscriptionLocation = `position-${Math.random().toString(36).substring(2, 15)}`;
      const payload= {
        exchangeCode: position.instrument_identifier === "INDICES" ? 1 : getExchangeCode(position.exchange),
        instrumentToken: position.instrument_token,
      }
      console.log("payload",payload,position.instrument_identifier);
      const handler = octopusInstance.wsHandler({
        messageType: "CompactMarketDataMessage",
        subscriptionLocation: subscriptionLocation,
        payload: payload
      });
   
      // Subscribe to market data updates
      handler.subscribe(({ msg }) => {
        if (!msg || !position) return;
        // console.log("randomposition", msg);
        
        // Format the position object first
        const formattedPosition = getPositionsValuesFormatted(position);
        
        // Create a position key to ensure we're updating the right position
        const positionKey = `${formattedPosition.exchange}-${formattedPosition.token}-${formattedPosition.product}`;
        const positionIndex = positionsMap[positionKey];
        
        // Skip if we can't find the position
        if (positionIndex === undefined || !positionsRef.current[positionIndex]) return;
        
        const currentPosition = positionsRef.current[positionIndex];
        const prevLTP = currentPosition.ltp || 0;
        
        // Ensure we have valid LTP values with proper fallbacks
        const currLTP = (msg.ltp !== undefined && msg.ltp !== null && msg.ltp > 0) ? msg.ltp : prevLTP;
        const isLTPHigh = prevLTP <= currLTP;
        
        // Use appropriate bid/ask price based on position direction with fallbacks
        let ltp = currLTP; // Default fallback
        if (currentPosition.net_quantity > 0) {
          // For long positions, use bid price if available
          ltp = (msg.bidPrice !== undefined && msg.bidPrice !== null && msg.bidPrice > 0) 
            ? msg.bidPrice 
            : currLTP;
        } else if (currentPosition.net_quantity < 0) {
          // For short positions, use ask price if available
          ltp = (msg.askPrice !== undefined && msg.askPrice !== null && msg.askPrice > 0) 
            ? msg.askPrice 
            : currLTP;
        }
        
        // Calculate previous_close from absoluteChange if available
        let previous_close = currentPosition.previous_close;
        if (msg.ltp !== undefined && msg.absoluteChange !== undefined) {
          const calculated = msg.ltp - msg.absoluteChange;
          if (calculated > 0) {
            previous_close = calculated;
          }
        }
        
        // Create position data with updated values
        const positionData = {
          ...currentPosition,
          ltp: ltp,
          previous_close: previous_close,
          isLTPHigh: isLTPHigh
        };
        
        // Format the position with the current position type
        const updatedPosition = getFormattedPosition(
          positionData,
          ltp,  // Explicitly pass the LTP value
          positionTypeRef.current
        );
        
        // Update the position in the array
        positionsRef.current[positionIndex] = updatedPosition;
        
        // Create a new array for the dispatch
        const updatedPositions = [...positionsRef.current];
        
        // Dispatch updated positions
        dispatch({
          type: "SET_POSITIONS_UPDATE",
          payload: updatedPositions
        });
        
        // Recalculate P&L with updated positions
        calculateAndUpdatePnL();
      });

      return handler;
    }).filter(Boolean); // Remove null handlers
  };

  const setupPositionHandler = () => {
    try {
      const clientId = localStorage.getItem("login_id");
      const authToken = localStorage.getItem("auth_token");
      
      if (!clientId || !authToken) {
        console.log('Missing clientId or authToken for position WebSocket');
        return;
      }

      // Force close any existing connection
      if (positionHandlerRef.current) {
        positionHandlerRef.current.unsubscribe();
        positionHandlerRef.current = null;
      }

      positionHandlerRef.current = octopusInstance.wsHandler({
        messageType: "PositionUpdate",
        subscriptionLocation: "positions",
        payload: {}
      });

      if (positionHandlerRef.current) {
        positionHandlerRef.current.subscribe(({ msg }) => {
          console.log('Position update received:', msg);
          const latestPositions = positionsRef.current || [];
          const updates = Array.isArray(msg) ? msg : [msg];
          const updatedPositions = [...latestPositions];
          const currentPositionType = positionTypeRef.current;
          
          updates.forEach(update => {
            // Ensure we have required fields for position identification
            if (!update.token || !update.exchange || !update.product) {
              console.warn('Received position update missing required fields:', update);
              return;
            }

            // Find the existing position
            const existingIndex = updatedPositions.findIndex(pos => 
              pos.token === update.token && 
              pos.exchange === update.exchange &&
              pos.product === update.product
            );
            
            if (existingIndex !== -1) {
              // Check if position should be removed (net_quantity is 0 or position is squared off)
              if (update.net_quantity === 0 || Math.abs(update.net_quantity || 0) < 0.0001) {
                const removedPosition = updatedPositions[existingIndex];
                console.log('Removing squared-off position:', {
                  symbol: removedPosition.trading_symbol || removedPosition.symbol,
                  product: removedPosition.product,
                  previousPnL: removedPosition.pnl || removedPosition.unrealizedPnL || 0,
                  net_quantity: update.net_quantity
                });
                // Remove the position from the array
                updatedPositions.splice(existingIndex, 1);
                return;
              }
              
              // Get the existing position
              const existingPosition = updatedPositions[existingIndex];
              
              // Merge the update with existing position data
              const mergedPosition = {
                ...existingPosition,
                ...update,
                // Preserve these fields from existing position if not in update
                ltp: existingPosition.ltp, // Always keep the existing LTP
                previous_close: existingPosition.previous_close, // Always keep the existing previous_close
                // Set realized PnL to zero
                realized_mtm: 0,
                realised_mtm: 0,
                realizedPnL: 0,
                // Only keep unrealized PnL
                unrealized_mtm: update.unrealized_mtm || existingPosition.unrealized_mtm,
                unrealised_mtm: update.unrealised_mtm || existingPosition.unrealised_mtm,
                unrealizedPnL: update.unrealizedPnL || existingPosition.unrealizedPnL,
                // Set pnl to be equal to just the unrealized value
                pnl: update.unrealized_mtm || existingPosition.unrealized_mtm || 0
              };
              
              // Format the position with the current position type
              const formattedPosition = getFormattedPosition(
                mergedPosition, 
                mergedPosition.ltp, // Use the preserved LTP
                currentPositionType
              );
              
              // Update the position in the array
              updatedPositions[existingIndex] = formattedPosition;
            } else {
              // For new positions, only add if net_quantity is not zero
              if (update.net_quantity !== 0 && Math.abs(update.net_quantity || 0) >= 0.0001) {
                // For new positions, use the average price as LTP if no LTP is provided
                const ltp = update.ltp || update.average_buy_price || update.average_sell_price || 0;
                
                // For new positions, ensure realized PnL is always zero
                const formattedPosition = getFormattedPosition(
                  {
                  ...update,
                  ltp: ltp,
                  previous_close: ltp, // Use the same value for previous_close initially
                  realized_mtm: 0,     // Explicitly set to zero
                  realised_mtm: 0,     // Explicitly set to zero
                  realizedPnL: 0       // Explicitly set to zero
                  }, 
                  ltp, 
                  currentPositionType
                );
                
                // Add the new position to the array
                updatedPositions.push(formattedPosition);
                console.log('New position added:', formattedPosition);
              } else {
                console.log('Skipping position with zero quantity:', update);
              }
            }
          });

          console.log('Position update processed. Updated positions count:', updatedPositions.length);
          
          // Update the positions ref immediately before any calculations
          positionsRef.current = updatedPositions;
          
          // Dispatch both updates to ensure all components are notified
          dispatch({
            type: "SET_POSITIONS",
            payload: updatedPositions
          });

          dispatch({
            type: "SET_POSITIONS_UPDATE",
            payload: updatedPositions
          });
          
          // After position structure changes, we need to update market data subscriptions
          setupMarketDataSubscriptions();
          
          // Recalculate P&L with the updated positions (including removals)
          calculateAndUpdatePnL();
        });

        console.log('Position subscription set up successfully');
        return positionHandlerRef.current;
      }
    } catch (error) {
      console.error('Position handler setup error:', error);
    }
  };

  // Initialize both handlers when auth state changes
  useEffect(() => {
    if (auth?.isLoggedIn) {
      setupPositionHandler();
      // Market data subscriptions will be set up when positions are loaded
    }
    
    return () => {
      // Clean up on unmount
      if (positionHandlerRef.current) {
        positionHandlerRef.current.unsubscribe();
        positionHandlerRef.current = null;
      }
      
      // Clean up market data handlers
      if (marketDataHandlersRef.current.length) {
        marketDataHandlersRef.current.forEach(handler => {
          if (handler && typeof handler.unsubscribe === 'function') {
            handler.unsubscribe();
          }
        });
        marketDataHandlersRef.current = [];
      }
    };
  }, [auth?.isLoggedIn]);

  // Initial setup with retries
  useEffect(() => {
    const attemptConnection = () => {
      const authToken = localStorage.getItem("auth_token");
      const clientId = localStorage.getItem("login_id");
      
      if (authToken && clientId && !positionHandlerRef.current && setupAttempts.current < 3) {
        setupAttempts.current += 1;
        setupPositionHandler();
      }
    };

    // Initial attempt
    attemptConnection();

    // Retry after a short delay
    const retryTimer = setInterval(() => {
      if (!positionHandlerRef.current && setupAttempts.current < 3) {
        attemptConnection();
      }
    }, 1000);

    return () => {
      clearInterval(retryTimer);
      if (positionHandlerRef.current) {
        positionHandlerRef.current.unsubscribe();
        positionHandlerRef.current = null;
      }
      
      // Clean up market data handlers
      if (marketDataHandlersRef.current.length) {
        marketDataHandlersRef.current.forEach(handler => {
          if (handler && typeof handler.unsubscribe === 'function') {
            handler.unsubscribe();
          }
        });
        marketDataHandlersRef.current = [];
      }
    };
  }, []);

  // Reconnect on visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setupAttempts.current = 0; // Reset attempts counter
        setupPositionHandler();
        setupMarketDataSubscriptions();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Recalculate P&L when fund data changes
  useEffect(() => {
    calculateAndUpdatePnL();
  }, [fundsViewV2]);

  return null;
}

export default usePositionUpdates;